import {
  useStudentMiniCardViewQuery,
  StudentMiniCardViewQuery,
} from 'core/graphql';

/** The non-null card payload (identity + groups + finance + tasks + notes). */
export type IStudentMiniCard = NonNullable<
  StudentMiniCardViewQuery['studentMiniCard']
>;

interface IOptions {
  /** Defer the request (e.g. until a hover-popover actually opens). */
  skip?: boolean;
}

interface IUseStudentMiniCard {
  card: IStudentMiniCard | null;
  loading: boolean;
  failed: boolean;
  refetch: () => void;
}

/**
 * Aggregated student mini-card for the communicator hover-card (spec 0009).
 * One round-trip per student; `cache-first` so reopening a popover renders
 * instantly. `finance` comes back null for viewers without payment roles —
 * consumers hide the money section, they don't treat it as "no debt".
 */
export const useStudentMiniCard = (
  studentId?: string,
  options?: IOptions,
): IUseStudentMiniCard => {
  const { data, loading, error, refetch } = useStudentMiniCardViewQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId || options?.skip,
    fetchPolicy: 'cache-first',
  });

  return {
    card: data?.studentMiniCard ?? null,
    loading,
    failed: Boolean(error),
    refetch: () => {
      refetch().catch(() => undefined);
    },
  };
};
